import React, { useMemo } from "react";

import { MarkType } from "@/sx-layout/models";

type MarkIconProps = {
  markTypeId: number;
  color: string;
  active: boolean;
  selected: boolean;
  outline?: boolean;
  className?: string;
};

export const MarkIcon: React.FC<MarkIconProps> = ({
  className,
  markTypeId,
  color,
  active,
  selected,
  outline = true,
}) => {
  const iconBackgroundColor = "#ffffff";
  const svgStyle = {
    borderRadius: "50%",
    backgroundColor: selected ? (active ? "#666" : "#e3844e") : iconBackgroundColor,
    boxShadow: selected ? `0px 0px 2px 2px ${active ? "#666" : "#e3844e"}` : "none",
  };

  const outlineStyle = {
    fill: outline ? (active ? "#666" : "#e3844e") : "transparent",
  };

  const markStyle = useMemo(() => {
    return { fill: color || Object.values(MarkType).find((mark) => mark.id === markTypeId)?.color };
  }, [markTypeId, color]);

  switch (markTypeId) {
    // 高所
    case MarkType.HEIGHT.id: {
      return (
        <svg style={svgStyle} className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <g>
            <path
              style={outlineStyle}
              d="M100,50c0,27.61-22.39,50-50,50S0,77.61,0,50,22.39,0,50,0s50,22.39,50,50ZM50,6C25.7,6,6,25.7,6,50s19.7,44,44,44,44-19.7,44-44S74.3,6,50,6Z"
            />
            <circle style={{ fill: iconBackgroundColor }} cx="50" cy="50" r="44" />
            <g>
              <path
                style={markStyle}
                d="M28.88,32.79l.25-.29h4.83l.25.25v3.28h8.04l5.81-.11.25.25v3.94l-.25.25-5.81-.11h-21.24l-5.77.11-.25-.29v-3.94l.29-.22,5.73.11h7.86v-3.24ZM35.45,63.82h-7.25v1.57h-4.47l.14-4.56v-2.26l-.07-2.59.22-.22,3.53.11h7.57l3.82-.11.25.22-.07,2.59v2.19l.07,2.04c.76-.04,1.52-.07,1.91-.15.4-.07.61-.07.61-.66v-7.36h-20.23v12.87h-5.12l.18-8.13v-4.23l-.11-4.12.25-.26,4.62.11h20.74l4.62-.11.25.26-.11,4.12v3.1l.14,5.1c0,3.32-2.09,4.08-6.92,4.08-.58,0-.65-.04-.79-.4l-1.37-3.14-2.42-.07ZM21.96,49.78l-.25-.29.18-3.35v-1.42l-.11-3.06.25-.26,3.9.11h11.43l3.9-.11.25.26-.11,3.06v1.53l.11,3.21-.29.33-4.11-.11h-10.96l-4.18.11ZM36.53,46.43v-1.68h-9.92v1.68h9.92ZM34.84,60.65v-1.64h-6.64v1.64h6.64Z"
              />
              <path
                style={markStyle}
                d="M57.23,53.9c-.14,5.98-.72,9.19-2.6,12.8-.14.26-.25.4-.43.4s-.4-.15-.68-.36l-3.28-2.55c2.02-3.9,2.34-6.82,2.34-13.45v-5.61l-.11-3.5.25-.25,4.26.11h5.41l4.25-.11.25.25-.11,3.5v4.48l.18,5.76h-5.01v-1.46h-4.72ZM51.5,39.14l-.25-.22v-4.23l.22-.18,4.94.11h6.17l4.8-.11.22.18v4.19l-.25.26-4.76-.11h-6.17l-4.91.11ZM61.95,45.77h-4.69v3.86h4.69v-3.86ZM73.53,48.8c-.11,8.79-2.02,13.53-6.49,17.68-.18.18-.32.25-.51.25-.14,0-.32-.11-.58-.29l-3.32-2.55c4.69-4.23,6.09-7.62,6.09-15.53v-6.93l-.11-6.05.25-.26,3.71.62c2.99-.58,5.7-1.42,8.08-2.55h.36c1.19.95,2.2,1.86,3.43,3.25l-.04.36c-3.53,1.35-7.47,2.41-10.89,2.99v4.81h7.32l3.89-.11.25.26v3.9l-.25.25-3.46-.11v9.41l.18,8.75h-5.23l.18-8.75v-9.41h-2.89Z"
              />
            </g>
          </g>
        </svg>
      );
    }
    // 酸欠
    case MarkType.OXYGEN_DEFICIENCY.id: {
      return (
        <svg style={svgStyle} className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <g>
            <path
              style={outlineStyle}
              d="M100,50c0,27.61-22.39,50-50,50S0,77.61,0,50,22.39,0,50,0s50,22.39,50,50ZM50,6C25.7,6,6,25.7,6,50s19.7,44,44,44,44-19.7,44-44S74.3,6,50,6Z"
            />
            <circle style={{ fill: iconBackgroundColor }} cx="50" cy="50" r="44" />
            <g>
              <path
                style={markStyle}
                d="M26.81,40.78h.65l3.1-.11.36.29-.11,5.1v1.88c2.09-.98,3.6-2.53,4.07-4.85-.9.07-1.8.18-2.7.25l-.32-.11-1.04-3.76.25-.33c.97-.04,1.91-.04,2.84-.07.86-1.88,1.76-4.12,2.3-6.22l.32-.14,4.36,1.16.11.33c-.76,1.59-1.55,3.15-2.41,4.63,1.37-.11,2.7-.22,4.07-.4-.43-.51-.94-.98-1.51-1.48l.04-.33,2.77-1.92.43.11c2.05,1.84,3.53,3.62,5.11,5.93l-.11.36-3.06,2.21-.43-.04c-.36-.61-.68-1.19-1.01-1.7-.22.07-.4.11-.61.14v3.43c0,.76.07.83.65.83.65,0,.94-.07,1.15-2.31l3.24,1.37c.36.14.4.25.36.61-.14,1.99-.36,2.57-1.04,3.4-.61.76-1.66,1.01-4.28,1.01-3.56,0-4.61-.29-4.61-3.58l.07-2.78v-1.23c-.94.11-1.87.25-2.81.36l1.8.72.11.33c-.29,1.95-1.12,3.65-2.27,4.99l3.71,1.37.07.4c-.18.43-.36.9-.58,1.34h3.02l2.63-.18,2.05,1.95c.14.11.18.22.18.4,0,.14-.04.33-.18.61-1.08,2.28-2.27,4.23-3.67,5.89,1.62.83,3.46,1.52,5.65,2.13l-2.52,3.83c-.22.29-.4.47-.72.47-.11,0-.25-.04-.43-.11-1.94-.87-3.78-1.92-5.4-3.18-2.12,1.55-4.65,2.71-7.74,3.62-.14.04-.25.07-.36.07-.22,0-.32-.11-.54-.4l-2.2-3-3.92-.07h-6.01v2.78h-3.92l.18-13.09v-7.67l-.11-5.13.25-.25,3.1.11h.65v-2.57h-.58l-4,.11-.25-.22v-3.51l.22-.22,4.03.11h8.1l4.03-.11.22.18v3.58l-.22.18-4.03-.11h-.54v2.57ZM26.34,52.89c-2.34,0-2.66-.61-2.66-3.58v-5.17h-.76v2.02c0,5.03-.47,6.47-2.02,7.92-.14.14-.22.22-.32.22-.14,0-.25-.14-.43-.4l-.47-.69v2.1h7.24v-2.42h-.58ZM19.68,44.14v6.58c.76-.9.86-1.99.86-4.48v-2.1h-.86ZM19.68,58.97v1.34h7.24v-1.34h-7.24ZM22.92,38.21v2.57h.76v-2.57h-.76ZM26.92,44.14h-.83v3.91c0,.61.07.69.76.69h.07v-4.59ZM30.81,57.7c2.48-2.28,4.28-5.13,5.4-8.32-.86.9-1.84,1.59-2.81,2.1-.18.11-.32.14-.43.14-.25,0-.43-.14-.58-.33l-1.58-1.88v8.28ZM30.92,63.38c2.2-.61,4.36-1.41,6.3-2.68-.5-.58-.97-1.23-1.4-1.92-.72.9-1.44,1.7-2.23,2.39-.22.18-.36.29-.5.29-.11,0-.25-.11-.43-.29l-1.8-1.81.07,4.01ZM38.41,55.42c.5.94,1.19,1.81,1.91,2.57.68-.8,1.26-1.63,1.76-2.57h-3.67Z"
              />
              <path
                style={markStyle}
                d="M81.69,66.63c-.18.25-.36.36-.58.36-.14,0-.29-.04-.43-.07-5.73-1.92-10.12-5.79-12.53-10.52-2.27,4.84-6.63,8.53-13.43,10.49-.18.07-.29.07-.4.07-.25,0-.32-.11-.47-.33l-2.99-3.83c10.84-2.6,13.9-8.53,13.9-15.11v-3.11l.32-.25h4.83l.25.29v1.95c0,1.08-.07,2.13-.18,3.15,2.66,7.27,7.71,10.96,15.02,12.58l-3.31,4.34ZM61.41,43.09c-1.55,2.53-3.31,4.77-5.11,6.4-.25.22-.47.33-.65.33-.22,0-.36-.07-.54-.22l-3.6-2.68c3.67-3.33,7.2-8.79,9.15-14.32l.36-.11,4.61,1.63.07.36c-.54,1.37-1.19,2.78-1.87,4.19h14.08l3.28-.18,2.27,2.1c.14.14.25.4.25.69,0,.14-.04.33-.11.47-1.84,3.07-4.14,6.18-6.27,8.57-.14.14-.25.22-.4.22-.18,0-.36-.11-.58-.25l-3.74-2.35c1.69-1.66,3.06-3.4,4.03-4.85h-15.23Z"
              />
            </g>
          </g>
        </svg>
      );
    }
    // 火気
    case MarkType.FIRE.id: {
      return (
        <svg style={svgStyle} className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <g>
            <path
              style={outlineStyle}
              d="M100,50c0,27.61-22.39,50-50,50S0,77.61,0,50,22.39,0,50,0s50,22.39,50,50ZM50,6C25.7,6,6,25.7,6,50s19.7,44,44,44,44-19.7,44-44S74.3,6,50,6Z"
            />
            <circle style={{ fill: iconBackgroundColor }} cx="50" cy="50" r="44" />
            <g>
              <path
                style={markStyle}
                d="M34.98,33.73c0,6.1-.07,9.93-.35,13,1.84,7.77,7.33,13.36,15.09,16l-3.33,4.37c-.14.18-.28.25-.43.25s-.28-.07-.42-.14c-5.88-2.64-10.31-6.72-12.79-12.57-2.34,6.57-8.29,11.02-13.74,12.79-.14.04-.25.07-.35.07-.21,0-.39-.11-.57-.33l-3.08-3.9c7.23-2.31,12.11-6.25,13.6-12.64.96-4.08,1.13-8.02.99-16.87l.28-.29h4.82l.28.25ZM25.87,43.12l.04.43c-.99,3.14-2.09,5.56-4.07,8.52h-.42l-3.83-2.46-.04-.33c1.77-2.53,2.83-4.8,3.75-7.69l.46-.04,4.11,1.55ZM46.84,42.97l.07.36c-1.74,3.72-2.73,5.31-4.57,7.73-.21.29-.43.36-.6.36-.21,0-.39-.07-.53-.18l-3.47-2.28c1.84-2.42,3.26-4.88,4.43-8.02l.32-.07,4.36,2.1Z"
              />
              <path
                style={markStyle}
                d="M77.51,36.18l4.68-.11.21.18v4.05l-.21.18-4.68-.11h-18.21c-.32.43-.67.87-1.03,1.26l4.6.11h10.91l4.68-.11.21.18v3.61l-.21.18-4.68-.11h-10.91l-4.64.11-.25-.22v-3.4c-.74.87-1.49,1.63-2.2,2.31-.21.22-.43.29-.6.29s-.35-.07-.53-.22l-3.29-2.46c2.55-2.17,5.31-5.45,7.23-9.35l.32-.07,4.25,1.55.07.4c-.32.58-.67,1.16-1.03,1.73h15.3ZM71.56,52.94l.07.36c-1.13,1.84-2.44,3.5-3.86,5.06,1.88,1.44,3.65,2.93,5.21,4.33l-.04.36-3.15,3.36h-.35c-1.49-1.52-3.26-3.14-5.17-4.77-2.94,2.38-6.09,4.19-9.07,5.27-.14.04-.25.07-.35.07-.28,0-.5-.18-.71-.43l-2.66-3.43c3.4-1.05,6.45-2.53,9.07-4.48-1.81-1.41-3.65-2.67-5.38-3.68v-.36l2.48-2.75.35-.04c1.95,1.08,4,2.38,5.99,3.76.57-.61,1.1-1.23,1.59-1.88.64-.83,1.17-1.7,1.7-2.6h-9l-4.99.11-.25-.22v-3.97l.21-.22,5.03.11h16.01l4-.11.25.25-.11,3.9c0,3.25.21,5.35.99,8.31.39,1.44.71,1.81,1.17,1.81.42,0,.99-.69,1.17-4.73l2.8,2.35c.46.4.46.51.42.94-.39,5.6-1.98,7.3-4.5,7.3-2.69,0-4.96-1.77-6.2-7.44-.6-2.71-.85-5.71-.78-8.38h-5.21l3.26,1.84Z"
              />
            </g>
          </g>
        </svg>
      );
    }
    // RT
    case MarkType.RT.id: {
      return (
        <svg style={svgStyle} className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <g>
            <path
              style={outlineStyle}
              d="M100,50c0,27.61-22.39,50-50,50S0,77.61,0,50,22.39,0,50,0s50,22.39,50,50ZM50,6C25.7,6,6,25.7,6,50s19.7,44,44,44,44-19.7,44-44S74.3,6,50,6Z"
            />
            <circle style={{ fill: iconBackgroundColor }} cx="50" cy="50" r="44" />
            <g>
              <path
                style={markStyle}
                d="M40.36,69.2l-5.77-14.03h-3.7v14.03h-8.88V30.8h12.98c7.94,0,13.57,3.59,13.57,11.27,0,5.46-2.37,8.66-6.17,10.29l8.29,16.84h-10.31ZM30.88,48.62h3.16c3.5,0,5.63-1.53,5.63-5.61s-2.12-5.81-5.63-5.81h-3.16v11.42Z"
              />
              <path style={markStyle} d="M82,37.94h-10.36v31.26h-8.88v-31.26h-10.36v-7.14h29.61v7.14Z" />
            </g>
          </g>
        </svg>
      );
    }
    // 仮置
    case MarkType.TEMPORARY_PLACEMENT.id: {
      return (
        <svg style={svgStyle} className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <g>
            <path
              style={outlineStyle}
              d="M100,50c0,27.61-22.39,50-50,50S0,77.61,0,50,22.39,0,50,0s50,22.39,50,50ZM50,6C25.7,6,6,25.7,6,50s19.7,44,44,44,44-19.7,44-44S74.3,6,50,6Z"
            />
            <circle style={{ fill: iconBackgroundColor }} cx="50" cy="50" r="44" />
            <g>
              <path
                style={markStyle}
                d="M23.99,63.5c2.45-3.89,3.49-6.98,3.49-16.05v-7.71l-.11-4.92.25-.26,5.4.11h10.04l5.58-.11.29.22v4.22l-.29.22-5.58-.11h-10.58v3.64h9.86l2.63-.18,1.87,1.76c.4.37.43.62.32,1.36-.76,5.44-2.23,9.29-4.5,12.96,1.62,1.54,3.78,2.97,6.69,4.3l-2.95,3.89c-.22.29-.47.4-.68.4-.18,0-.36-.07-.58-.18-2.27-1.17-4.17-2.72-5.72-4.26-1.66,1.69-3.63,3.23-5.79,4.37-.4.18-.61.29-.79.29s-.29-.11-.5-.33l-2.73-3.01c-.43.84-.9,1.65-1.44,2.46-.14.22-.29.29-.43.29-.11,0-.25-.04-.4-.18l-3.35-2.86v3.64h-5.04l.18-11.35v-4.55c-.54.84-1.01,1.69-1.69,2.5-.22.22-.36.33-.5.33s-.25-.11-.36-.37l-1.58-4.41c2.91-4.52,5.22-9.99,6.83-16.93l.32-.18,4.35.99.18.33c-.76,3.53-1.69,6.43-2.7,9v20.68ZM32.48,47.08c-.04,3.53-.18,7.05-.79,10.5-.36,2.06-.94,4.11-1.83,6.02,2.3-1.21,4.46-2.75,6.29-4.63-1.8-2.9-3.06-6.21-3.56-9.44l.14-.33,4.03-1.18.29.15c.47,2.02,1.26,4.3,2.3,6.28,1.19-2.2,1.94-4.66,2.37-7.38h-9.25Z"
              />
              <path
                style={markStyle}
                d="M76.29,48.26l3.89-.07.25.26-.11,3.2v5.95l.11,3.71-.29.22-3.85-.07h-12.55l-3.85.07-.25-.22.11-3.71v-5.95l-.11-3.27.25-.18,3.85.07h1.47c.07-.44.18-.88.25-1.32h-8.71l-5.07.11-.25-.26v-3.34l.29-.22,5.04.11h9.21c.07-.51.11-1.03.18-1.54h-7.01l-5.5.11-.25-.26.11-3.01v-2.42l-.11-2.72.25-.22,5.5.11h17.84l5.47-.11.29.29-.11,2.64v2.42l.11,3.01-.29.26-5.47-.11h-5.65c-.07.51-.18,1.03-.29,1.54h8.06l5.36-.11.29.22v3.38l-.29.22-5.36-.11h-8.74c-.07.44-.18.88-.25,1.32h6.19ZM79.71,62.51l5-.11.29.22v3.53l-.29.22-5-.11h-21.87v1.25h-5.14l.18-7.64v-9.18l.25-.26h4.42l.29.29v11.79h21.87ZM58.24,36.76v1.69h3.35v-1.69h-3.35ZM64.42,50.86v1.1h11.01v-1.1h-11.01ZM64.42,55.38h11.01v-1.1h-11.01v1.1ZM64.42,58.83h11.01v-1.14h-11.01v1.14ZM69.6,36.76h-3.42v1.69h3.42v-1.69ZM77.73,38.45v-1.69h-3.53v1.69h3.53Z"
              />
            </g>
          </g>
        </svg>
      );
    }
    // 危険物
    case MarkType.HAZARDOUS_MATERIAL.id: {
      return (
        <svg style={svgStyle} className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <g>
            <path
              style={outlineStyle}
              d="M100,50c0,27.61-22.39,50-50,50S0,77.61,0,50,22.39,0,50,0s50,22.39,50,50ZM50,6C25.7,6,6,25.7,6,50s19.7,44,44,44,44-19.7,44-44S74.3,6,50,6Z"
            />
            <circle style={{ fill: iconBackgroundColor }} cx="50" cy="50" r="44" />
            <g>
              <path
                style={markStyle}
                d="M30.91,44.15l4.53-.08.19.19v2.92l-.19.19-4.53-.08h-13.49v3.41c0,4.79-1.04,8.81-4.2,12.33-.11.11-.19.17-.27.17-.05,0-.11-.03-.16-.08l-2.78-2.5c2.59-2.81,3.55-5.59,3.55-10.02v-4.24c-.38.22-.76.47-1.17.72-.14.08-.22.11-.33.11-.14,0-.25-.08-.35-.25l-1.69-2.73c3.69-2.12,5.51-3.96,7.97-7.32l.27-.08,3.25,1.05.11.25c-.22.33-.46.66-.68.99h3.71l2.54-.14,1.83,1.43c.16.14.25.25.25.41,0,.19-.11.39-.33.69-.79.99-1.45,1.82-2.24,2.67h4.23ZM22.17,44.15c.74-.85,1.26-1.54,1.69-2.15h-5.27c-.68.77-1.39,1.46-2.16,2.15h5.73ZM27.93,51.89h-5.3v5.45c0,1.68.16,1.68,3.93,1.68,3.33,0,4.56-.05,5.16-.58.52-.44.63-1.21.71-2.53l3.14,1.13c.3.11.52.22.49.61-.08,2.01-.68,3.39-2.27,4.24-1.09.55-3.3.63-7.02.63-7.23,0-7.97-.5-7.97-4.51l.05-3.66v-2.37l-.08-3.06.19-.19,4.18.08h4.29l4.18-.08.19.19-.08,3.06v1.07l.05,1.6c0,2.34-1.5,2.95-5.19,2.95-.38,0-.46-.06-.55-.3l-1.04-2.92c1.09,0,1.8-.03,2.43-.08.36-.03.49-.14.49-.55v-1.84Z"
              />
              <path
                style={markStyle}
                d="M56.43,48.28h1.91l3.22-.08.19.19-.08,2.48v2.09l.08,2.59-.22.25-3.11-.08h-1.58c1.5,1.82,3.6,3.17,6.52,4.18l-1.97,2.89c-.14.19-.25.28-.41.28-.08,0-.19-.03-.33-.08-2.57-1.16-4.64-2.92-5.98-5.04-1.42,2.34-3.77,4.07-7.15,5.15-.14.03-.25.06-.33.06-.14,0-.25-.06-.35-.22l-1.97-2.62c3.85-1.13,6.09-2.62,7.12-4.6h-.96l-3.17.08-.19-.22.14-2.7v-2.01l-.08-2.48.19-.19,3.22.08h1.77v-1.38l-2.84.08-.19-.17v-.94c-.6.44-1.2.8-1.83,1.16-.14.08-.25.11-.33.11-.16,0-.27-.14-.46-.36l-1.2-1.54c-.25.63-.49,1.27-.76,1.93,1.45,2.45,1.86,3.91,1.86,6.36,0,3.17-1.56,4.54-3.71,4.54-.41,0-.55-.08-.66-.41l-.85-2.73.14,8.15h-3.77l.19-10.3v-10.74l-.08-4.13.19-.19,3.44.08h2.4l1.69-.08,1.45,1.4c.14.11.19.25.19.41,0,.14-.03.28-.08.47-.36,1.32-.71,2.59-1.15,3.83,3.03-1.87,5.1-4.1,6.63-6.96l.27-.06,3.22.85.08.25c-.11.19-.19.41-.3.61,1.77,1.84,4.07,3.39,7.34,4.82l-1.61,2.59c-.22.33-.36.44-.55.44-.14,0-.27-.06-.49-.17-.6-.3-1.17-.61-1.72-.96v1.6l-.16.14-2.87-.08v1.38ZM41.93,41.12v11.67l.03,1.76c1.42-.03,1.94-.3,1.94-1.73,0-.99-.25-2.42-1.36-4.68-.19-.41-.33-.63-.33-.91,0-.16.05-.33.14-.58.71-2.04,1.17-3.58,1.58-5.53h-1.99ZM51.16,50.76v2.45h1.58c.11-.83.14-1.62.16-2.45h-1.75ZM57.82,44.12c-1.15-.83-2.16-1.79-3.08-2.84-.79,1.05-1.69,1.98-2.65,2.86l1.04.03h3.11l1.58-.06ZM56.4,50.76c-.03.83-.05,1.62-.11,2.45h1.94v-2.45h-1.83Z"
              />
              <path
                style={markStyle}
                d="M86.01,44.95c-.52,5.67-1.69,8.97-3.22,11.53-1.39,2.34-3,4.05-5.38,5.81-.27.19-.46.28-.63.28-.14,0-.25-.08-.38-.19l-2.16-2.15c2.16-1.46,3.96-3.03,5.49-5.29,1.39-2.06,2.59-4.95,3.06-9.99h-.63c-.44,3.55-1.23,5.34-2.02,6.8-.9,1.65-2.1,3.22-3.47,4.43-.25.19-.38.28-.52.28-.08,0-.16-.05-.25-.14l-2.18-2.09c1.56-1.13,2.73-2.45,3.6-4.02.74-1.32,1.23-2.53,1.75-5.26h-.41c-.74,1.79-1.56,3.36-2.35,4.51-.19.28-.36.41-.49.41-.11,0-.22-.06-.33-.17l-2.29-1.87c.35-.47.68-.99,1.01-1.57l-1.5-.05v4.18c.76-.25,1.56-.5,2.37-.77l-.03,2.89c0,.33-.11.47-.38.58-.57.25-1.23.5-1.97.77l.14,9.22h-3.66l.11-8.04c-1.01.36-2.07.69-3.11,1.02l-.25-.05-1.42-3.28.16-.28c1.61-.36,3.11-.72,4.64-1.1v-5.18h-.76c-.22,1.57-.44,3-.68,4.18-.05.22-.16.33-.36.33-.11,0-.27-.03-.44-.14l-2.48-1.32c.57-2.75,1.01-6.11,1.12-9.52l.19-.06,2.95.72.08.3c-.05.74-.14,1.49-.22,2.2h.6v-5.7l.16-.22h3.06l.16.19v5.7l1.94-.06.14.14v2.2c1.23-2.48,2.24-5.56,2.68-8.04l.22-.17,3.22.74.08.28c-.3,1.29-.68,2.59-1.12,3.83h6.61l3.3-.08.22.25c-.05,5.97-.03,12.83-.52,15.97-.25,1.57-.6,2.75-1.36,3.61s-1.72,1.4-4.61,1.4c-.44,0-.63-.08-.76-.41l-1.45-3.33c1.23.03,1.97.03,2.84-.05,1.01-.08,1.45-.33,1.69-1.71.49-2.78.55-7.68.52-12.47h-.33Z"
              />
            </g>
          </g>
        </svg>
      );
    }
    // 石綿
    case MarkType.ASBESTOS.id: {
      return (
        <svg style={svgStyle} className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <g>
            <path
              style={outlineStyle}
              d="M100,50c0,27.61-22.39,50-50,50S0,77.61,0,50,22.39,0,50,0s50,22.39,50,50ZM50,6C25.7,6,6,25.7,6,50s19.7,44,44,44,44-19.7,44-44S74.3,6,50,6Z"
            />
            <circle style={{ fill: iconBackgroundColor }} cx="50" cy="50" r="44" />
            <g>
              <path
                style={markStyle}
                d="M32.75,39.65c-.93,2.92-2.18,5.8-3.82,8.31h11.46l5.5-.11.25.26-.11,5.03v5.36l.18,8.42h-5.61v-2.04h-12.11v2.41h-5.46l.18-8.24v-3.86c-1.29,1.42-2.68,2.73-4.25,3.97-.11.11-.21.15-.32.15-.18,0-.36-.18-.54-.4l-3.11-3.75c3.18-2.22,5.89-4.85,8.07-7.84,1.75-2.37,2.96-4.92,3.86-7.66h-4.18l-5.93.11-.25-.26v-4.38l.25-.25,5.93.11h19.57l5.89-.11.25.25v4.38l-.25.26-5.89-.11h-9.57ZM40.61,52.44h-12.11v7.95h12.11v-7.95Z"
              />
              <path
                style={markStyle}
                d="M56.28,54.89l.21.26c-.21,3.72-.71,6.78-1.64,9.55-.14.4-.25.55-.46.55-.11,0-.21-.04-.39-.11l-3-1.39c.96-2.99,1.46-5.87,1.68-9.3l.21-.18,3.39.62ZM61.36,34.4c-1.25,2.44-2.71,4.99-4,6.93.36.29.71.58,1.07.91.86-1.53,1.68-3.1,2.5-4.74l.32-.15,3.75,1.97.14.33c-1.89,3.32-4.11,6.56-6.04,9.08.86-.07,1.68-.15,2.5-.26-.25-.58-.46-1.13-.75-1.64l.11-.29,2.57-1.31.32.07c1,1.68,2.07,4.3,2.75,6.56l3.21.11h2.89v-2.04h-1.54l-4.32.11-.25-.29.18-3.32v-6.74l-.11-2.99.25-.26,4.04.11h.5c.43-1.28.75-2.44,1.04-3.83l.29-.22,4.57.95.14.33c-.36,1.02-.75,1.93-1.18,2.77h3l4.04-.11.25.26-.11,2.99v6.85l.11,3.17-.29.33-4.25-.11h-1.64v2.04h3.5l3.79-.11.25.26-.11,3.54v1.2l.14,4.08c0,3.43-1.43,4.27-4.75,4.27-.54,0-.75-.07-.93-.62l-1.36-3.76c.75,0,1.54-.04,1.89-.07.39-.04.46-.22.46-.8v-3.83h-2.89v3.25l.18,8.09h-5.07l.18-8.09v-3.25h-2.39v8.71h-4.54l.07-3.03-2.79,1.2-.25-.18c-.32-2.59-.75-5.32-1.32-7.58l.18-.26,2.43-.95.32.15c.68,2.22,1.14,3.97,1.46,5.87l.04-1.64v-2.77l-.07-2.99-2.32,1.13-.36-.18c-.14-.58-.29-1.13-.43-1.64-.54.11-1.04.18-1.54.29v6.27l.18,8.97h-4.68l.18-8.97v-5.58c-1.43.22-2.93.4-4.5.58l-.32-.18-.89-3.9.25-.26c.93-.04,1.82-.07,2.68-.11.75-1.02,1.43-2.11,2.14-3.17-1.61-1.57-3.21-2.84-5.18-4.08l-.04-.29,1.89-3.03.32-.04c.32.18.61.36.93.58,1-2.08,1.89-4.12,2.71-6.42l.32-.15,4,1.57.14.33ZM71.43,41.54h7.25v-1.42h-7.25v1.42ZM78.68,46.35v-1.49h-7.25v1.49h7.25Z"
              />
            </g>
          </g>
        </svg>
      );
    }
    // 汎用1
    case MarkType.OTHER_1.id: {
      return (
        <svg style={svgStyle} className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <g>
            <path
              style={outlineStyle}
              d="M100,50c0,27.61-22.39,50-50,50S0,77.61,0,50,22.39,0,50,0s50,22.39,50,50ZM50,6C25.7,6,6,25.7,6,50s19.7,44,44,44,44-19.7,44-44S74.3,6,50,6Z"
            />
            <circle style={{ fill: iconBackgroundColor }} cx="50" cy="50" r="44" />
            <polygon style={markStyle} points="50 84.1 20 31.9 80 31.9 50 84.1" />
          </g>
        </svg>
      );
    }
    // 汎用2
    case MarkType.OTHER_2.id: {
      return (
        <svg style={svgStyle} className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <g>
            <path
              style={outlineStyle}
              d="M100,50c0,27.61-22.39,50-50,50S0,77.61,0,50,22.39,0,50,0s50,22.39,50,50ZM50,6C25.7,6,6,25.7,6,50s19.7,44,44,44,44-19.7,44-44S74.3,6,50,6Z"
            />
            <circle style={{ fill: iconBackgroundColor }} cx="50" cy="50" r="44" />
            <polygon
              style={markStyle}
              points="50 19.5 57.08 41.27 80 41.27 61.46 54.73 68.54 76.5 50 63.04 31.46 76.5 38.54 54.73 20 41.27 42.92 41.27 50 19.5"
            />
          </g>
        </svg>
      );
    }
    // 汎用3
    case MarkType.OTHER_3.id: {
      return (
        <svg style={svgStyle} className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <g>
            <path
              style={outlineStyle}
              d="M100,50c0,27.61-22.39,50-50,50S0,77.61,0,50,22.39,0,50,0s50,22.39,50,50ZM50,6C25.7,6,6,25.7,6,50s19.7,44,44,44,44-19.7,44-44S74.3,6,50,6Z"
            />
            <circle style={{ fill: iconBackgroundColor }} cx="50" cy="50" r="44" />
            <path
              style={markStyle}
              d="M50,40.54c2.69-8.07,7.58-12.73,14.8-12.73s12.2,7.83,12.2,14.39c0,14.49-8.78,24.97-27,37.99-18.22-13.02-27-23.5-27-37.99,0-6.56,5.17-14.39,12.2-14.39,7.22,0,12.11,4.66,14.8,12.73Z"
            />
          </g>
        </svg>
      );
    }
  }

  return (
    <svg style={svgStyle} className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
      <g>
        <path
          style={outlineStyle}
          d="M100,50c0,27.61-22.39,50-50,50S0,77.61,0,50,22.39,0,50,0s50,22.39,50,50ZM50,6C25.7,6,6,25.7,6,50s19.7,44,44,44,44-19.7,44-44S74.3,6,50,6Z"
        />
      </g>
    </svg>
  );
};
